import { Button } from "@solomon-labs/design/components/ui";
import React, { useLayoutEffect, useRef, useState } from "react";

/**
 * A raised + light-bar action button whose label tracks a status flow
 * (idle → pending → success/error), with the light-bar driven by the same
 * status. The width is frozen on mount (rounded up to an even px) so the
 * changing label never resizes the button mid-flow and the light-bar's two
 * halves stay on integer boundaries. Used for one-shot async actions like a
 * verify or submit step.
 *
 * Crucially it is NOT wrapped in any `overflow-hidden` box — the light-bar
 * renders 4px *below* the button, so a clipping ancestor (like a height-collapse
 * wrapper) would cut it off. Render it bare.
 */
export function LightBarActionButton({
    label,
    status,
    disabled,
    onClick,
    className,
    reserveLabels,
    icon,
    size = "md"
}: {
    label: string;
    status: "idle" | "pending" | "success" | "error";
    disabled?: boolean;
    onClick: () => void;
    className?: string;
    /** Underlying Button size — defaults to `md` (the verify/submit standard). */
    size?: "md" | "lg";
    /** Optional element rendered after the label (e.g. a retry glyph). Sits
     *  outside the reserve pile, so it adds to — never resizes — the locked
     *  width; keep the reserve set wide enough to absorb it. */
    icon?: React.ReactNode;
    /**
     * Every label this button can cycle through. When provided, the button
     * reserves width for the WIDEST of them (rendered invisibly, stacked in a
     * one-cell grid), so the visible label changing never resizes it — and so
     * separately-mounted instances (e.g. a state showcase) all share one
     * width. Without it, width locks to whatever label is present at mount.
     */
    reserveLabels?: string[];
}): React.ReactElement {
    const ref = useRef<HTMLButtonElement>(null);
    const [lockedWidth, setLockedWidth] = useState<number | null>(null);
    // Lock the width (before paint), rounded up to an even px, so:
    //  - the label changing ("Verify Ownership" → "Verifying" → "Verified")
    //    never resizes the button; and
    //  - the even width keeps the light-bar's two 50% halves on integer
    //    boundaries (an odd width bleeds ~1px past the right edge).
    // With `reserveLabels` the measured intrinsic width is the widest label
    // (the hidden pile forces it) regardless of which one is showing; without
    // it, the lock is the mount-time label. Re-measures if the reserve set
    // changes (e.g. EOA → multisig switches the candidate labels).
    const reserveKey = reserveLabels?.join(" ") ?? "";
    useLayoutEffect(() => {
        const el = ref.current;
        if (!el) return;
        // Clear any prior lock so offsetWidth reports the intrinsic content
        // width (the pile's widest label), then round up to even.
        el.style.width = "auto";
        const w = el.offsetWidth;
        setLockedWidth(w % 2 === 0 ? w : w + 1);
    }, [reserveKey]);
    const reserve = reserveLabels && reserveLabels.length > 0;
    // Visible label + optional icon, together — so the icon counts toward the
    // *measured* content rather than tacking on beyond a label-only lock.
    const visible = (
        <span className="inline-flex items-center justify-center gap-1.5 whitespace-nowrap">
            {label}
            {icon}
        </span>
    );
    const labelContent = reserve ? (
        // All candidates + the visible label share one grid cell; the cell
        // sizes to the widest, so the button's content width is label- (and
        // icon-) independent as long as the widest reserve label absorbs them.
        <span className="grid">
            {reserveLabels.map((candidate, i) => (
                <span
                    key={`${candidate}-${i}`}
                    aria-hidden
                    className="invisible col-start-1 row-start-1 whitespace-nowrap"
                >
                    {candidate}
                </span>
            ))}
            <span className="col-start-1 row-start-1 justify-self-center">
                {visible}
            </span>
        </span>
    ) : (
        visible
    );
    return (
        <Button
            ref={ref}
            variant="raised"
            interaction="light-bar"
            size={size}
            status={status}
            disabled={disabled}
            onClick={onClick}
            className={["self-start", className].filter(Boolean).join(" ")}
            style={
                lockedWidth !== null && lockedWidth !== undefined
                    ? { width: lockedWidth }
                    : undefined
            }
        >
            {labelContent}
        </Button>
    );
}
